/* THE VAULT AND PROFILE FILTERS — the definitions, the predicates, the
 * option lists and the orderings, as data rather than as four copies of
 * the same three lines.
 *
 * ── ⚠⚠ WHY THIS IS A MODULE AND NOT MORE LINES IN `filters.js` ───────
 * Two filters shipped for months with NO TEST AT ALL: the only assertion
 * anywhere was that the string `data-vtype` appears in the sheet.
 * Neither predicate was ever exercised. Jesse ruled 2026-09-20 that the
 * gate covers EVERY filter, *"two filters have been shipping with no test at
 * all and I would rather find that out now than the next time one
 * breaks"* — and a predicate buried in a render function cannot be
 * tested without rendering. So the logic lives here and the views call
 * it.
 *
 * ── ⚠⚠⚠ THE TWO RULES THE APP HAS NEVER HAD TO FOLLOW, BOTH HIS ─────
 * 1. **`undefined` NEVER BECOMES AN OPTION.** `backType` exists on 259
 *    of 11,614 catalogue rows, so most notes do not carry it, and
 *    `[...new Set(items.map(n => n.backType))]` would offer `undefined`
 *    as something to filter by.
 * 2. **A NOTE WITHOUT THE FIELD MUST STILL APPEAR UNDER "All".** This is
 *    absent-not-empty arriving in the interface: a note that says
 *    nothing about its back type has not said "none", and hiding it
 *    from an unfiltered list would be the interface inventing a value
 *    the data does not have.
 *
 * ⚠ And an option group with no options is HIDDEN, not rendered empty —
 * a collector with no National Bank Notes should not see a Back Type
 * heading over nothing.
 */

/* ── denomination ordering, moved here from `filters.js` so it can be
 * tested. ⚠ `views-vault.js` imports `denomValue` from `filters.js`,
 * which re-exports it, so no caller changed. */
const DENOM_ORDER = ['3¢', '5¢', '10¢', '15¢', '25¢', '50¢', '$1', '$2', '$5', '$10', '$20', '$50', '$100', '$500', '$1,000', '$5,000', '$10,000']
const isColDen = (d) => (d || '').slice(0, 2) === 'C:'
const denomText = (d) => (isColDen(d) ? d.slice(2) : d)
function colOrd(d) {
  if (d === '—') return 99999
  const F = d.match(/^\$(\d+)\/(\d+)$/); if (F) return (+F[1]) / (+F[2])
  const D = d.match(/^\$(\d+)/); if (D) return 1000 + (+D[1])
  const m = d.match(/^(\d+)s(?:(\d+)d)?/); if (m) return 20000 + (+m[1]) * 12 + (+(m[2] || 0))
  const p = d.match(/^(\d+)d/); if (p) return 20000 + (+p[1])
  const L = d.match(/^£\s?(\d+)/); if (L) return 50000 + (+L[1])
  return 90000 + d.charCodeAt(0)
}
export function denomValue(d) {
  if (isColDen(d)) return 1e18 + colOrd(denomText(d))
  if (!d || d === '—') return 1e17
  if (d.charAt(d.length - 1) === '¢') return parseFloat(d) / 100
  const f = d.match(/^\$(\d+)\/(\d+)$/); if (f) return (+f[1]) / (+f[2])
  const m = d.match(/([\d,]+)/); return m ? +m[1].replace(/,/g, '') : 9e15
}
export const denomLabel = (d) => (d === '—' ? 'Error' : denomText(d))

/**
 * ⚠⚠⚠ CHARTER PERIOD WAS A FILTER AND IS NOT ANY MORE — HIS RULING,
 * 2026-09-20, AND THE FIELD STAYS IN THE CATALOGUE.
 *
 * His reasoning: for Large Size Nationals the two are essentially the
 * same thing — First Charter is `Original` or `1875`, Second is `1882`,
 * Third is `1902` — so offering both spends sheet space on a distinction
 * that is not one. And `series` is catalogue-wide (5,991 rows) where
 * `charterPeriod` is Nationals-only (386), so the narrow one is the
 * cheaper to drop.
 *
 * ⚠ IT IS A DISPLAY DECISION, NOT A DATA ONE. `charterPeriod` is
 * written, deployed and correct on all 386 rows and nothing here
 * touches it. **To bring the filter back:** restore a def
 * `{ key: 'charter', v: 'vCharter', p: 'pCharter', group: 'Charter
 * period', of: (n) => n.charterPeriod, rank: listRank(['First',
 * 'Second', 'Third']) }`, the two state fields, and the gate's charter
 * lines. ⚠⚠ It is NOT left here behind a flag: a filter that can never
 * be set is the same shape as a gate line that cannot fail.
 */

/**
 * ⚠⚠ BACK TYPE SORTS IN ISSUE ORDER, NOT ALPHABETICAL — his ruling:
 * that is how the book presents them and how collectors think about
 * them. Alphabetical would read Brown · Date · Plain · Value and put the
 * Third Charter's Plain Back before the Second Charter's Value Back.
 */
export const BACK_ORDER = Object.freeze(['Brown Back', 'Date Back', 'Value Back', 'Plain Back'])

/**
 * ⚠⚠⚠ SERIES SORTS CHRONOLOGICALLY, AND `Original` IS NOT A YEAR.
 * His ruling: `Original` first — it is 1863–1875 — then years ascending,
 * **with lettered years after their base**: 1928, 1928-A, 1928-B, 1934.
 * ⚠ A plain string sort puts `Original` after `2017` and `1928-A` before
 * `1928`, which is wrong twice over.
 */
export function seriesValue(s) {
  const v = String(s ?? '')
  if (/^Original$/i.test(v)) return -1
  const m = v.match(/^(\d{4})(?:-([A-Z]))?$/)
  if (!m) return Number.POSITIVE_INFINITY
  return Number(m[1]) * 100 + (m[2] ? m[2].charCodeAt(0) - 64 : 0)
}

const listRank = (order) => (v) => {
  const i = order.indexOf(v)
  /* ⚠ An unlisted value sorts LAST and keeps its place, rather than
   * collapsing to 0 and appearing first as though it were expected. */
  return i < 0 ? order.length : i
}

/**
 * THE FOUR FILTERS. `v` and `p` are the state fields for the vault and
 * the public profile; `of` reads the value off a catalogue note.
 * ⚠ `type` and `denom` are the two that have been shipping untested.
 */
export const FILTER_DEFS = Object.freeze([
  Object.freeze({ key: 'type', v: 'vType', p: 'pType', group: 'Type', of: (n) => n.section, rank: null }),
  Object.freeze({ key: 'denom', v: 'vDenom', p: 'pDenom', group: 'Denomination', of: (n) => n.fd || n.denom, rank: denomValue }),
  /* ⚠ HIS 7, 2026-09-27 (stop C): *"The Series filter reads the same year
   * the app shows, Colonial dates included."* The filter offered only what
   * `series` carried — 5,991 rows then, 6,359 after the amendment — while
   * 3,916 more rows showed a year the app reads from `desc` (Colonial
   * 2,856 among them). So `seriesOf`: the row's `series` where it carries
   * one, else the year `desc` states, as a string — the same source and
   * order as `yearOf`, which the sort and the card title already use.
   * Measured 2026-10-03: 6,359 → 10,300 rows carry a value; the catalogue
   * offers 184 values, not 69; no row's `series` year differs from its
   * `desc` year, so the order of the two never decides anything today. */
  Object.freeze({ key: 'series', v: 'vSeries', p: 'pSeries', group: 'Series', of: (n) => seriesOf(n), rank: seriesValue }),
  Object.freeze({ key: 'back', v: 'vBack', p: 'pBack', group: 'Back type', of: (n) => n.backType, rank: listRank(BACK_ORDER) }),
])

export const defOf = (key) => FILTER_DEFS.find((d) => d.key === key) ?? null

/**
 * Does this note pass this filter at this value?
 *
 * ⚠⚠ `All` PASSES EVERYTHING, INCLUDING A NOTE THAT CARRIES NOTHING.
 * That is his rule, and it is the whole of absent-not-empty in the
 * interface: a note with no `backType` has not said "none", so an
 * unfiltered list must still contain it.
 */
export function matches(note, def, value) {
  if (value === undefined || value === null || value === 'All') return true
  return def.of(note) === value
}

/** Apply every filter at once. `get(def)` returns the current value. */
export function applyFilters(notes, get, noteOf = (x) => x) {
  let out = notes
  for (const def of FILTER_DEFS) out = out.filter((x) => matches(noteOf(x), def, get(def)))
  return out
}

/**
 * The options a group should offer, ordered.
 *
 * ⚠⚠⚠ A VALUE THAT IS ABSENT IS NOT AN OPTION. `undefined`, `null` and
 * the empty string are dropped before anything else happens — the day
 * that stops being true, the sheet offers a button labelled
 * `undefined` that matches the notes nobody meant to select.
 */
export function optionsFor(notes, def) {
  const seen = new Set()
  for (const n of notes) {
    const v = def.of(n)
    if (v === undefined || v === null || v === '') continue
    seen.add(v)
  }
  const rank = def.rank
  return [...seen].sort((a, b) => {
    if (rank) {
      const d = rank(a) - rank(b)
      if (d) return d
    }
    return a > b ? 1 : a < b ? -1 : 0
  })
}

/**
 * ⚠ A GROUP WITH NO OPTIONS IS HIDDEN. Type and Denomination always have
 * values because every note has a section and a denomination; the two
 * new ones do not, and a collector with no National Bank Notes would
 * otherwise see a "Charter period" heading over an empty row.
 */
export const groupHasOptions = (notes, def) => optionsFor(notes, def).length > 0

/* ══ THE YEAR A NOTE SORTS BY ═══════════════════════════════════════
 *
 * ⚠⚠⚠ HIS RULING, 2026-09-20, AND THE REASONING IS THE WHOLE POINT OF
 * THE RECOVERY: *we added an authoritative year to 5,936 rows precisely
 * because `desc` never had one, and the sort still reads `desc`.*
 * `noteYear` looks for a four-digit year in the description. For the
 * rows the recovery just filled that description is `Bruce / Gilfillan`
 * — no digits at all — so 5,876 of those 5,936 rows sorted as though
 * they had no year, LAST in both directions.
 *
 * ── THE ORDER OF PREFERENCE, HIS ────────────────────────────────────
 *   1. the collector's own `year` — **theirs, and it overrides
 *      everything**. Nothing here writes it; ~~`backfillYears` and
 *      `custom.js` are untouched, so a derived value never becomes
 *      the collector's.~~ ⚠ CORRECTED 2026-09-23, HIS RULING: `custom.js`'s
 *      `convertToRow` now fills an EMPTY year from `yearOf` when a note
 *      converts onto a row, and `startAdd` does the same for a note added
 *      from Resources — the same source as the sort, because the 7.3(b)
 *      render showed a converted note with no year under a hero printing
 *      *Series 1934-D*, and the Resources path had the identical gap. A
 *      typed year is still never written over; `backfillYears` is still
 *      untouched.
 *   2. `series`, WHEN IT IS A YEAR
 *   3. a year in `desc` — exactly as before
 *   4. no year
 */

/**
 * The year a `series` states, or null.
 *
 * ⚠⚠⚠ `Original` IS NOT A YEAR AND IS NOT COERCED INTO ONE — his
 * ruling, and the source backs it: the only thing stated for those 55
 * rows is a RANGE, `### Notes issued 1865–1878 (Original Series)`,
 * under `# FIRST CHARTER PERIOD (1863–1882)`. No row carries a year and
 * no heading names one. **Picking an end of a 14-year span would be
 * inventing precision the source does not have** — the same rule that
 * forbids two measurements becoming a range. So `Original` sorts as a
 * no-year note, which is what it is.
 */
export function seriesYear(series) {
  const m = String(series ?? '').match(/^(\d{4})(?:-[A-Z])?$/)
  return m ? Number(m[1]) : null
}

/** A year in the description — the rule as it has always been.
 *  ⚠ It matches 1700–1999 only, so it never saw a 2000s series even
 *  when one was written there. `seriesYear` is what covers those now. */
export function yearFromDesc(desc) {
  const m = String(desc ?? '').match(/\b(1[7-9]\d\d)\b/)
  return m ? Number(m[1]) : null
}

/** HIS 7 (stop C): what the Series filter reads off a note — `series` as
 *  the row carries it (`1928-A`, `Original`), else the year its `desc`
 *  states, else nothing (undefined: never an option, his rule 1 above). */
export function seriesOf(note) {
  const n = note || {}
  if (n.series) return n.series
  const d = yearFromDesc(n.desc)
  return d ? String(d) : undefined
}

/** ⚠ OTHER'S STEP 6, HIS RULING 7: *"The Series filter reads the holding's
 *  year for every custom note."* — and his A: *"the holding's year first,
 *  then what the filter reads today, the same order as the sort; nothing
 *  that filters today should disappear."* The note the filters see: a
 *  custom note with a year of its own is a COPY whose `series` is that
 *  year, so `seriesOf` reads it first and falls back as it always did; a
 *  custom note without one, and every catalogue note, is the note itself.
 *  His B: *"a stranger's custom notes filter by their year too"* — the
 *  profile passes the public year (`views-community.js`, `filters.js`). */
export const filterNote = (note, ownYear) => (note && note.cust && Number(ownYear) ? { ...note, series: String(Number(ownYear)) } : note)

/** The year a note sorts by, in his order of preference, or null. */
export function yearOf(ownYear, note) {
  const own = Number(ownYear)
  if (own) return own
  const s = seriesYear(note && note.series)
  if (s) return s
  return yearFromDesc(note && note.desc)
}

/* ══ THE DETAIL LINE — WHAT A NOTE SAYS ABOUT ITSELF ════════════════
 *
 * ⚠⚠ HIS RULING, 2026-09-20: *a National in my vault looks like any
 * other note, and a Brown Back doesn't say so when I open it.* The
 * catalogue carries `series`, `backType` and `charterPeriod` and nothing
 * displayed them, so a collector could FILTER to Brown Backs and then
 * get no confirmation on the note itself.
 *
 * ── HIS THREE RULINGS, BUILT ────────────────────────────────────────
 * 1. **EVERYWHERE — all 5,992 rows, not Nationals only.** *`Series 1963`
 *    under a small-size FRN is the note's identity, not clutter, and a
 *    line that appears or vanishes by section is harder to explain than
 *    one that always shows what the row carries.*
 * 2. **`Series 1882 · Brown Back · Second Charter Period`** — series
 *    first, then the distinguishing back, then the umbrella. ⚠ And
 *    **`Original Series`, not `Series Original`** — that is how the book
 *    says it.
 * 3. **SUPPRESS THE 115 DUPLICATES.** *The display should not say the
 *    same thing twice, and suppressing is cheaper and safer than editing
 *    115 `desc` values that are correct as they stand.*
 *
 * ⚠⚠⚠ ABSENT, NOT EMPTY, ONE LAST TIME: a note with no back type shows
 * NOTHING there — not a label with nothing after it — and a note with
 * none of the three returns the empty string, so the view renders no
 * element at all. The hero already does this for `desc`; this follows it.
 */

/** Does the description OPEN with this value? `1875 · Allison / New …`
 *  ⚠ "Opens with" rather than "contains", because the rule is about the
 *  display saying the same thing twice AT THE START. Measured
 *  2026-09-20: both readings give the SAME 115 rows, so nothing today
 *  rests on the difference — which is the reason to take the tighter one
 *  now, while it is free. */
export const descOpensWith = (desc, value) =>
  String(desc ?? '').split('·')[0].trim() === String(value)

/**
 * The line a note shows about itself, or `''` when it has nothing to say.
 *
 * ⚠⚠ THE `Original Series` WORDING IS UNREACHABLE ON TODAY'S DATA, AND
 * THAT IS DECLARED RATHER THAN HIDDEN. All 55 rows whose series is
 * `Original` have a `desc` that OPENS with `Original`, so his ruling 3
 * suppresses the segment before his ruling 2's wording can apply. The
 * wording is still right — it is what the book says, and it matters the
 * day a row carries `Original` without it in `desc` — so it is kept and
 * proven on a SYNTHETIC note, exactly as the refusals elsewhere are.
 */
/** ⚠ HIS RULING 4, 2026-10-02: *"On Confederate, Colonial and Demand Note
 *  rows, the added year reads as the year alone ("1861", "1776 · Georgia"),
 *  not "Series 1861"."* — and, by his answer the same day, *"Ruling 4
 *  applies to the note screen's detail line as well."* These three sections
 *  gained their `series` from the book's date or Act heading (the stop B
 *  amendment's D), and a note of theirs has no "Series" printed on it. ONE
 *  list, read by this line and by the card's rows (`note-narrow.js`). */
export const YEAR_ALONE_SECTIONS = Object.freeze(new Set(['Confederate States', 'Colonial', 'Demand Note']))
/** The series as a line prints it: the book's *Original Series*, the year
 *  alone on his three sections, *Series YYYY* everywhere else. */
export const seriesText = (series, section) => (series === 'Original' ? 'Original Series' : YEAR_ALONE_SECTIONS.has(section) ? String(series) : `Series ${series}`)

export function detailLine(note) {
  const n = note || {}
  const parts = []
  if (n.series && !descOpensWith(n.desc, n.series)) {
    parts.push(seriesText(n.series, n.section))
  }
  /* ⚠ STOP C (C1), his answer 6 of 2026-09-27: *"the seal in the detail
   * line ("Series 1914 · Blue Seal")"* — after the series, before the back
   * type. The catalogue carries no `seal` yet (register item (b), the seal
   * repair, on his go), so today this prints on no row and is proven on a
   * synthetic note. The wording is the card's row wording (`ROW_TEXT.seal`
   * in note-narrow.js, which imports this file and cannot be imported
   * back); `filter-check` asserts the two agree. */
  if (n.seal) parts.push(`${n.seal} Seal`)
  if (n.backType) parts.push(n.backType)
  if (n.charterPeriod) parts.push(`${n.charterPeriod} Charter Period`)
  return parts.join(' · ')
}

/* ══ THE ISSUE ON A ROW — HIS ANSWER 9, 2026-09-27 ══════════════════
 * *"Yes, Hawaii / North Africa on rows and cards"*, and his 6: the issue
 * in the vault card's sub line (*"WWII Emergency Note · Hawaii · Fr.
 * 2300"*). The WWII rows carry no `issue` yet (register item (c), the WWII
 * issue repair, on his go), so today nothing prints and both are proven
 * on a synthetic row. ⚠ The Fractional rows carry an `issue` since the
 * amendment (*Third Issue*, 164 rows; no `desc` repeats it) and the card's
 * candidate rows already print it there (his I) — AND HIS RULING OF
 * 2026-10-03: *"Fractional notes show their issue on the vault card and
 * the Resources row too, the same way the WWII rows do."* Two sections,
 * named; nothing widens on its own. */
export const ISSUE_ON_ROW_SECTIONS = Object.freeze(new Set(['WWII Emergency Note', 'Fractional Currency']))
/** The issue a vault card or a Resources row shows, or ''. */
export const issueOnRow = (note) => (note && note.issue && ISSUE_ON_ROW_SECTIONS.has(note.section) ? String(note.issue) : '')
/** A Resources row's text: `desc`, with the issue after the series part
 *  where the row shows one (*1935-A · Hawaii · Julian / Morgenthau · …*). */
export function rowDesc(note) {
  const n = note || {}
  const desc = String(n.desc || '')
  const issue = issueOnRow(n)
  if (!issue) return desc
  const parts = desc ? desc.split(' · ') : []
  const at = /^\d{4}(-[A-Z])?$/.test(parts[0] || '') ? 1 : 0
  parts.splice(at, 0, issue)
  return parts.join(' · ')
}
